import { DOCUMENT } from '@angular/common';
import type { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import type { JwtConfig } from '@auth0/angular-jwt';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import { defer, map, mergeMap, of } from 'rxjs';

const fromPromiseOrValue = <T>(input: T | Promise<T>) => {
  if (input instanceof Promise) {
    return defer(() => input);
  }
  return of(input);
};

const standardPorts = ['80', '443'];

function isAllowedDomain(request: HttpRequest<unknown>, allowedDomains: Array<string | RegExp>, document: Document): boolean {
  const requestUrl = new URL(request.url, document.location.origin);
  if (requestUrl.host === document.location.host) {
    return true;
  }
  const hostName = `${requestUrl.hostname}${requestUrl.port && !standardPorts.includes(requestUrl.port) ? ':' + requestUrl.port : ''}`;
  return (
    allowedDomains.findIndex((domain: string | RegExp) =>
      typeof domain === 'string' ? domain === hostName : domain instanceof RegExp ? domain.test(hostName) : false,
    ) > -1
  );
}

function isDisallowedRoute(request: HttpRequest<unknown>, disallowedRoutes: Array<string | RegExp>, document: Document): boolean {
  const requestedUrl = new URL(request.url, document.location.origin);
  return (
    disallowedRoutes.findIndex((route: string | RegExp) => {
      if (typeof route === 'string') {
        const parsedRoute = new URL(route, document.location.origin);
        return parsedRoute.hostname === requestedUrl.hostname && parsedRoute.pathname === requestedUrl.pathname;
      }
      if (route instanceof RegExp) {
        return route.test(request.url);
      }
      return false;
    }) > -1
  );
}

export const jwtInterceptor: HttpInterceptorFn = (request, next) => {
  const config = inject<JwtConfig>(JWT_OPTIONS);
  const jwtHelper = inject(JwtHelperService);
  const document = inject<Document>(DOCUMENT);

  const tokenGetter = config.tokenGetter;
  const headerName: string = config.headerName || 'Authorization';
  const authScheme: string | ((req?: HttpRequest<unknown>) => string) =
    config.authScheme || config.authScheme === '' ? config.authScheme : 'Bearer ';
  const allowedDomains: Array<string | RegExp> = config.allowedDomains ?? [];
  const disallowedRoutes: Array<string | RegExp> = config.disallowedRoutes ?? [];
  const shouldThrowNoTokenError = config.throwNoTokenError || false;
  const shouldSkipWhenExpired = config.skipWhenExpired;

  if (!tokenGetter || !isAllowedDomain(request, allowedDomains, document) || isDisallowedRoute(request, disallowedRoutes, document)) {
    return next(request);
  }

  const token = tokenGetter(request);
  return fromPromiseOrValue(token).pipe(
    mergeMap(asyncToken => {
      const scheme = jwtHelper.getAuthScheme(authScheme, request);

      if (!asyncToken && shouldThrowNoTokenError) {
        throw new Error('Could not get token from tokenGetter function.');
      }

      let tokenIsExpired$ = of(false);
      if (shouldSkipWhenExpired) {
        tokenIsExpired$ = asyncToken ? fromPromiseOrValue(jwtHelper.isTokenExpired(asyncToken)) : of(true);
      }

      if (asyncToken) {
        return tokenIsExpired$.pipe(
          map(isExpired =>
            isExpired && shouldSkipWhenExpired
              ? request.clone()
              : request.clone({ setHeaders: { [headerName]: `${scheme}${asyncToken}` } }),
          ),
          mergeMap(innerRequest => next(innerRequest)),
        );
      }

      return next(request);
    }),
  );
};
